// IAM Support API Layer
// Centralized API helpers for IAM (Identity and Access Management) endpoints

import apiClient from '@lift-admin/utils/apiClient';
import { invalidateUserSidebarCache } from '@lift-admin/utils/apiClientDrawerV2';
import { Toastify } from 'lift-ui';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import { buildSummary, humanizeValue } from '@lift-admin/utils/logChangeFormatter';
import {
  Product,
  PermissionLevel,
  UserWithPermissions,
  UserPermission,
  Group,
  GroupPermission,
  PaginatedResponse,
  ApiResponse,
} from './types';

// Use apiClient directly with relative paths (same as user service)
// apiClient already has baseURL configured from process.env.VITE_BASEURL
// All paths should use /user/v1/ prefix to match user service pattern
const apiUrl = (path: string) => {
  // Remove leading slash if present, then prepend /user/v1/
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `/user/v1/${cleanPath}`;
};

/** Optional names so activity logs never fall back to bare IDs. */
export type IamLogContext = {
  userName?: string;
  userEmail?: string;
  groupName?: string;
  /** userId → display label */
  userLabels?: Record<number, string>;
  /** productId → product name */
  productNames?: Record<number, string>;
};

type LogChange = {
  label: string;
  from?: string;
  to?: string;
  added?: string[];
  removed?: string[];
};

function userDisplayName(ctx?: IamLogContext, userId?: number): string {
  if (ctx?.userName?.trim()) return ctx.userName.trim();
  if (ctx?.userEmail?.trim()) return ctx.userEmail.trim();
  if (userId != null && ctx?.userLabels?.[userId]) return ctx.userLabels[userId];
  return userId != null ? `User #${userId}` : 'User';
}

function groupDisplayName(ctx?: IamLogContext, groupId?: number): string {
  if (ctx?.groupName?.trim()) return ctx.groupName.trim();
  return groupId != null ? `Group #${groupId}` : 'Group';
}

function productDisplayName(
  productId: number,
  knownName?: string,
  ctx?: IamLogContext
): string {
  if (knownName?.trim()) return knownName.trim();
  if (ctx?.productNames?.[productId]) return ctx.productNames[productId];
  return `Product #${productId}`;
}

function labelForUserId(userId: number, ctx?: IamLogContext): string {
  return ctx?.userLabels?.[userId] || `User #${userId}`;
}

// =============================================================================
// PRODUCTS API
// =============================================================================

/**
 * Fetch all available products/functionalities that can have permissions assigned
 */
export async function getProducts(): Promise<Product[]> {
  try {
    const response = await apiClient.get<ApiResponse<Product[]>>(
      apiUrl('/product_permissions/products')
    );
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch products';
    Toastify('error', message);
    throw error;
  }
}

/**
 * Fetch all available permission levels (Viewer, Editor, Manager)
 */
export async function getPermissionLevels(): Promise<PermissionLevel[]> {
  try {
    const response = await apiClient.get<ApiResponse<PermissionLevel[]>>(
      apiUrl('/product_permissions/levels')
    );
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch permission levels';
    Toastify('error', message);
    throw error;
  }
}

// =============================================================================
// USER PRODUCT PERMISSIONS API
// =============================================================================

/**
 * Fetch paginated list of users with their permissions
 */
export async function getUsersWithPermissions(
  page: number = 1,
  limit: number = 20,
  search?: string
): Promise<PaginatedResponse<UserWithPermissions>> {
  try {
    const params: { page: number; limit: number; search?: string } = { page, limit };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    
    const response = await apiClient.get<ApiResponse<PaginatedResponse<UserWithPermissions>>>(
      apiUrl('/product_permissions/users'),
      { params }
    );
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch users';
    Toastify('error', message);
    throw error;
  }
}

/**
 * Fetch all users by fetching in batches (max 100 per page)
 * This is useful for dropdowns that need all users available
 * Note: getUsersWithPermissions already shows error toasts, so we don't duplicate them here
 * This ensures we fetch ALL users without any search filter, matching the user tab behavior
 */
export async function getAllUsersWithPermissions(): Promise<UserWithPermissions[]> {
  const allUsers: UserWithPermissions[] = [];
  const maxLimit = 100; // API max limit
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    // Explicitly pass undefined for search to ensure we get all users
    const response = await getUsersWithPermissions(page, maxLimit, undefined);
    const users = response?.data || [];
    allUsers.push(...users);

    // Check if there are more pages
    const totalPages = response?.pagination?.totalPages || 0;
    
    // Check if we've reached the last page
    // If we got fewer users than the limit, we're on the last page
    if (users.length < maxLimit) {
      hasMore = false;
    } else if (page >= totalPages && totalPages > 0) {
      hasMore = false;
    } else if (totalPages === 0) {
      hasMore = false;
    } else {
      hasMore = true;
    }
    
    page++;
    
    // Safety check to prevent infinite loops
    if (page > 1000) {
      console.warn('getAllUsersWithPermissions: Stopped after 1000 pages to prevent infinite loop');
      break;
    }
  }
  return allUsers;
}

/**
 * Fetch total count of superadmin users (for dashboard).
 * Uses paginated fetch so the count is accurate across all users, not just the current page.
 */
export async function getSuperadminCount(): Promise<number> {
  const allUsers = await getAllUsersWithPermissions();
  return allUsers.filter((u) => u.isSuperadmin === true || u.is_superadmin === true).length;
}

/**
 * Get a specific user's direct product permissions
 */
export async function getUserPermissions(userId: number): Promise<UserPermission[]> {
  try {
    const response = await apiClient.get<ApiResponse<UserPermission[]>>(
      apiUrl(`/product_permissions/users/${userId}/permissions`)
    );
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch user permissions';
    Toastify('error', message);
    throw error;
  }
}

/**
 * Set all product permissions for a user (replaces existing permissions)
 */
export async function setUserPermissions(
  userId: number,
  permissions: Array<{ productId: number; levelName: string }>,
  context?: IamLogContext
): Promise<UserPermission[]> {
  const subject = userDisplayName(context, userId);
  let previousPermissions: UserPermission[] = [];
  try {
    previousPermissions = await getUserPermissions(userId);
  } catch {
    previousPermissions = [];
  }
  try {
    const response = await apiClient.post<ApiResponse<UserPermission[]>>(
      apiUrl(`/product_permissions/users/${userId}/permissions`),
      { permissions }
    );
    Toastify('success', 'User permissions updated successfully');

    const resultPerms: UserPermission[] =
      response.data.response || (response.data as any) || [];
    const resultNameById = new Map(
      resultPerms.map((p) => [p.productId, p.productName] as const)
    );

    const oldMap = new Map<number, { level: string; name: string }>();
    previousPermissions.forEach((p) =>
      oldMap.set(p.productId, { level: p.levelName, name: p.productName })
    );
    const newMap = new Map<number, string>();
    permissions.forEach((p) => newMap.set(p.productId, p.levelName));

    const productIds = new Set<number>([...oldMap.keys(), ...newMap.keys()]);
    const changes: LogChange[] = [];
    productIds.forEach((pid) => {
      const oldLevel = oldMap.get(pid)?.level;
      const newLevel = newMap.get(pid);
      if (oldLevel !== newLevel) {
        changes.push({
          label: productDisplayName(
            pid,
            oldMap.get(pid)?.name || resultNameById.get(pid),
            context
          ),
          from: oldLevel ?? 'None',
          to: newLevel ?? 'None',
        });
      }
    });

    await logAdminAction(
      buildSummary('User permissions', subject, 'updated', changes.length),
      {
        url: `/product_permissions/users/${userId}/permissions`,
        method: 'POST',
        status: 200,
        category: 'user_management',
        action: 'update_user_permissions',
        userId,
        user_name: subject,
        user_email: context?.userEmail,
        permissionsCount: permissions.length,
        ...(changes.length
          ? { changes }
          : {
              details: permissions.length
                ? permissions.map((p) => ({
                    label: productDisplayName(
                      p.productId,
                      resultNameById.get(p.productId),
                      context
                    ),
                    value: p.levelName,
                  }))
                : [{ label: 'Permissions', value: 'None' }],
            }),
      }
    );
    
    return resultPerms;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to set user permissions';
    Toastify('error', message);
    
    await logAdminAction(
      `Failed to update permissions for "${subject}": ${message}`,
      {
        url: `/product_permissions/users/${userId}/permissions`,
        method: 'POST',
        category: 'user_management',
        action: 'update_user_permissions',
        userId,
        user_name: subject,
        error: message,
      },
      true
    );
    
    throw error;
  }
}

// =============================================================================
// GROUPS API
// =============================================================================

/**
 * Fetch paginated list of groups with members and permissions
 */
export async function getGroups(
  page: number = 1,
  limit: number = 20
): Promise<PaginatedResponse<Group>> {
  try {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Group>>>(
      apiUrl('/product_permissions/groups'),
      { params: { page, limit } }
    );
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch groups';
    Toastify('error', message);
    throw error;
  }
}

/**
 * Create a new user group
 */
export async function createGroup(
  groupName: string,
  description?: string
): Promise<Group> {
  try {
    const response = await apiClient.post<ApiResponse<Group>>(
      apiUrl('/product_permissions/groups'),
      { groupName, description }
    );
    Toastify('success', 'Group created successfully');
    
    await logAdminAction(
      buildSummary('Permission group', groupName, 'created'),
      {
        url: '/product_permissions/groups',
        method: 'POST',
        status: 200,
        category: 'user_management',
        action: 'create_permission_group',
        groupName,
        description,
        details: [
          { label: 'Name', value: humanizeValue(groupName) },
          { label: 'Description', value: humanizeValue(description) },
        ],
      }
    );
    
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to create group';
    Toastify('error', message);
    await logAdminAction(
      `Failed to create permission group "${groupName}": ${message}`,
      {
        category: 'user_management',
        action: 'create_permission_group',
        groupName,
        error: message,
      },
      true
    );
    throw error;
  }
}

/**
 * Update an existing group's name and/or description
 */
export async function updateGroup(
  groupId: number,
  groupName?: string,
  description?: string,
  previous?: { groupName?: string; description?: string }
): Promise<Group> {
  const subject = groupName || previous?.groupName || `Group #${groupId}`;
  try {
    const response = await apiClient.put<ApiResponse<Group>>(
      apiUrl(`/product_permissions/groups/${groupId}`),
      { groupName, description }
    );
    Toastify('success', 'Group updated successfully');

    const changes: LogChange[] = [];
    if (previous) {
      if (groupName !== undefined && previous.groupName !== groupName) {
        changes.push({
          label: 'Name',
          from: humanizeValue(previous.groupName),
          to: humanizeValue(groupName),
        });
      }
      if (description !== undefined && previous.description !== description) {
        changes.push({
          label: 'Description',
          from: humanizeValue(previous.description),
          to: humanizeValue(description),
        });
      }
    }

    await logAdminAction(
      buildSummary('Permission group', subject, 'updated', changes.length),
      {
        url: `/product_permissions/groups/${groupId}`,
        method: 'PUT',
        status: 200,
        category: 'user_management',
        action: 'update_permission_group',
        groupId,
        groupName: subject,
        ...(changes.length
          ? { changes }
          : {
              details: [
                ...(groupName
                  ? [{ label: 'Name', value: humanizeValue(groupName) }]
                  : []),
                ...(description !== undefined
                  ? [{ label: 'Description', value: humanizeValue(description) }]
                  : []),
              ],
            }),
      }
    );
    
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to update group';
    Toastify('error', message);
    await logAdminAction(
      `Failed to update permission group "${subject}": ${message}`,
      {
        category: 'user_management',
        action: 'update_permission_group',
        groupId,
        groupName: subject,
        error: message,
      },
      true
    );
    throw error;
  }
}

/**
 * Delete a group (also removes all memberships and permissions)
 */
export async function deleteGroup(
  groupId: number,
  context?: IamLogContext
): Promise<void> {
  const subject = groupDisplayName(context, groupId);
  try {
    await apiClient.delete(apiUrl(`/product_permissions/groups/${groupId}`));
    Toastify('success', 'Group deleted successfully');
    
    await logAdminAction(
      buildSummary('Permission group', subject, 'deleted'),
      {
        url: `/product_permissions/groups/${groupId}`,
        method: 'DELETE',
        status: 200,
        category: 'user_management',
        action: 'delete_permission_group',
        groupId,
        groupName: subject,
        details: [{ label: 'Group', value: subject }],
      }
    );
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to delete group';
    Toastify('error', message);
    await logAdminAction(
      `Failed to delete permission group "${subject}": ${message}`,
      {
        category: 'user_management',
        action: 'delete_permission_group',
        groupId,
        groupName: subject,
        error: message,
      },
      true
    );
    throw error;
  }
}

// =============================================================================
// GROUP MEMBERS API
// =============================================================================

/**
 * Add users to a group
 */
export async function addUsersToGroup(
  groupId: number,
  userIds: number[],
  context?: IamLogContext
): Promise<{ added: number; alreadyMembers: number }> {
  const groupLabel = groupDisplayName(context, groupId);
  const memberLabels = userIds.map((id) => labelForUserId(id, context));
  try {
    const response = await apiClient.post<ApiResponse<{ added: number; alreadyMembers: number }>>(
      apiUrl(`/product_permissions/groups/${groupId}/members`),
      { userIds }
    );
    const result = response.data.response || response.data as any;
    Toastify('success', `Added ${result.added} user(s) to group`);
    
    await logAdminAction(
      buildSummary('Permission group members', groupLabel, 'updated', memberLabels.length),
      {
        url: `/product_permissions/groups/${groupId}/members`,
        method: 'POST',
        status: 200,
        category: 'user_management',
        action: 'add_users_to_group',
        groupId,
        groupName: groupLabel,
        userIds,
        addedCount: result.added,
        changes: [{ label: 'Group Members', added: memberLabels }],
      }
    );
    
    return result;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to add users to group';
    Toastify('error', message);
    await logAdminAction(
      `Failed to add members to permission group "${groupLabel}": ${message}`,
      {
        category: 'user_management',
        action: 'add_users_to_group',
        groupId,
        groupName: groupLabel,
        error: message,
      },
      true
    );
    throw error;
  }
}

/**
 * Remove users from a group
 */
export async function removeUsersFromGroup(
  groupId: number,
  userIds: number[],
  context?: IamLogContext
): Promise<{ removed: number }> {
  const groupLabel = groupDisplayName(context, groupId);
  const memberLabels = userIds.map((id) => labelForUserId(id, context));
  try {
    const response = await apiClient.delete<ApiResponse<{ removed: number }>>(
      apiUrl(`/product_permissions/groups/${groupId}/members`),
      { data: { userIds } }
    );
    const result = response.data.response || response.data as any;
    Toastify('success', `Removed ${result.removed} user(s) from group`);
    
    await logAdminAction(
      buildSummary('Permission group members', groupLabel, 'updated', memberLabels.length),
      {
        url: `/product_permissions/groups/${groupId}/members`,
        method: 'DELETE',
        status: 200,
        category: 'user_management',
        action: 'remove_users_from_group',
        groupId,
        groupName: groupLabel,
        userIds,
        removedCount: result.removed,
        changes: [{ label: 'Group Members', removed: memberLabels }],
      }
    );
    
    return result;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to remove users from group';
    Toastify('error', message);
    await logAdminAction(
      `Failed to remove members from permission group "${groupLabel}": ${message}`,
      {
        category: 'user_management',
        action: 'remove_users_from_group',
        groupId,
        groupName: groupLabel,
        error: message,
      },
      true
    );
    throw error;
  }
}

// =============================================================================
// GROUP PERMISSIONS API
// =============================================================================

/**
 * Get a group's product permissions
 */
export async function getGroupPermissions(groupId: number): Promise<GroupPermission[]> {
  try {
    const response = await apiClient.get<ApiResponse<GroupPermission[]>>(
      apiUrl(`/product_permissions/groups/${groupId}/permissions`)
    );
    return response.data.response || response.data as any;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to fetch group permissions';
    Toastify('error', message);
    throw error;
  }
}

/**
 * Set all product permissions for a group (replaces existing permissions)
 */
export async function setGroupPermissions(
  groupId: number,
  permissions: Array<{ productId: number; levelName: string }>,
  context?: IamLogContext
): Promise<GroupPermission[]> {
  const subject = groupDisplayName(context, groupId);
  let previousPermissions: GroupPermission[] = [];
  try {
    previousPermissions = await getGroupPermissions(groupId);
  } catch {
    previousPermissions = [];
  }
  try {
    const response = await apiClient.post<ApiResponse<GroupPermission[]>>(
      apiUrl(`/product_permissions/groups/${groupId}/permissions`),
      { permissions }
    );
    Toastify('success', 'Group permissions updated successfully');

    const resultPerms: GroupPermission[] =
      response.data.response || (response.data as any) || [];
    const resultNameById = new Map(
      resultPerms.map((p) => [p.productId, p.productName] as const)
    );

    const oldMap = new Map<number, { level: string; name: string }>();
    previousPermissions.forEach((p) =>
      oldMap.set(p.productId, { level: p.levelName, name: p.productName })
    );
    const newMap = new Map<number, string>();
    permissions.forEach((p) => newMap.set(p.productId, p.levelName));

    const productIds = new Set<number>([...oldMap.keys(), ...newMap.keys()]);
    const changes: LogChange[] = [];
    productIds.forEach((pid) => {
      const oldLevel = oldMap.get(pid)?.level;
      const newLevel = newMap.get(pid);
      if (oldLevel !== newLevel) {
        changes.push({
          label: productDisplayName(
            pid,
            oldMap.get(pid)?.name || resultNameById.get(pid),
            context
          ),
          from: oldLevel ?? 'None',
          to: newLevel ?? 'None',
        });
      }
    });

    await logAdminAction(
      buildSummary('Group permissions', subject, 'updated', changes.length),
      {
        url: `/product_permissions/groups/${groupId}/permissions`,
        method: 'POST',
        status: 200,
        category: 'user_management',
        action: 'update_group_permissions',
        groupId,
        groupName: subject,
        permissionsCount: permissions.length,
        ...(changes.length
          ? { changes }
          : {
              details: permissions.length
                ? permissions.map((p) => ({
                    label: productDisplayName(
                      p.productId,
                      resultNameById.get(p.productId),
                      context
                    ),
                    value: p.levelName,
                  }))
                : [{ label: 'Permissions', value: 'None' }],
            }),
      }
    );
    
    return resultPerms;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to set group permissions';
    Toastify('error', message);
    await logAdminAction(
      `Failed to update permissions for group "${subject}": ${message}`,
      {
        category: 'user_management',
        action: 'update_group_permissions',
        groupId,
        groupName: subject,
        error: message,
      },
      true
    );
    throw error;
  }
}

// =============================================================================
// SUPERADMIN API (Note: Backend route needs to be confirmed)
// =============================================================================

/**
 * Promote/demote a user to/from superadmin (toggles status)
 * Endpoint: PUT /v1/admin/superadmin/:userId
 * Toggles is_superadmin status (if false → true, if true → false)
 * User must be active and admin verified
 */
export async function promoteToSuperadmin(
  userId: number,
  context?: IamLogContext
): Promise<void> {
  const fallbackName = userDisplayName(context, userId);
  try {
    const response = await apiClient.put<ApiResponse<{
      id: number;
      name: string;
      email: string;
      is_admin: boolean;
      is_superadmin: boolean;
      team: boolean;
    }>>(apiUrl(`/admin/superadmin/${userId}`));
    
    const result = response.data.response || response.data as any;
    const isSuperadmin = result?.is_superadmin;
    const subject = result?.name || fallbackName;
    const message = isSuperadmin 
      ? 'User promoted to Superadmin' 
      : 'User demoted from Superadmin';
    
    Toastify('success', message);
    
    await logAdminAction(
      isSuperadmin
        ? `User "${subject}" promoted to Superadmin`
        : `User "${subject}" demoted from Superadmin`,
      {
        url: `/admin/superadmin/${userId}`,
        method: 'PUT',
        status: 200,
        category: 'user_management',
        action: isSuperadmin ? 'promote_to_superadmin' : 'demote_from_superadmin',
        targetUserId: userId,
        targetUserName: subject,
        targetUserEmail: result?.email || context?.userEmail,
        newSuperadminStatus: isSuperadmin,
        changes: [
          {
            label: 'Superadmin',
            from: humanizeValue(!isSuperadmin, { type: 'boolean', boolStyle: 'yesNo' }),
            to: humanizeValue(isSuperadmin, { type: 'boolean', boolStyle: 'yesNo' }),
          },
        ],
      }
    );
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to update superadmin status';
    Toastify('error', message);
    await logAdminAction(
      `Failed to update Superadmin status for "${fallbackName}": ${message}`,
      {
        category: 'user_management',
        action: 'promote_to_superadmin',
        targetUserId: userId,
        user_name: fallbackName,
        error: message,
      },
      true
    );
    throw error;
  } finally {
    // Superadmin status changes what the sidebar shows; the PUT may have committed even on error.
    await invalidateUserSidebarCache(userId);
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Transform backend user permissions to a Record format for easy lookup
 */
export function transformPermissionsToRecord(
  permissions: UserPermission[] | GroupPermission[]
): Record<string, string> {
  return permissions.reduce((acc, perm) => {
    // Both UserPermission and GroupPermission have productKey
    acc[perm.productKey] = perm.levelName;
    return acc;
  }, {} as Record<string, string>);
}
